import { AnimatedCounter, ScrollReveal, SplitReveal } from '@/components/motion';
import { RETENTION_SERIES } from '@/data/experience';

/** The three retention series rendered as small accessible column charts. */
const RETENTION_CHARTS = [
  { key: 'mba', title: 'MBA (ACBSP)', bar: 'bg-brand-navy' },
  { key: 'dba', title: 'DBA (ACBSP)', bar: 'bg-brand-gold' },
  { key: 'institution', title: 'Institution-wide', bar: 'bg-brand-navy/40' },
] as const;

/**
 * RetentionDetail (/experience/student-achievement-rates ★ ACBSP-mandated) —
 * "Retention rate, year by year". Presents the full RETENTION_SERIES two ways:
 * three compact, fully accessible div-bar column charts (MBA navy, DBA gold,
 * institution muted-navy — each bar's aria-label and printed number carry the
 * value) mirroring the /experience achievement-rates idiom, then a semantic
 * <table> (Year / MBA / DBA / Institution-wide, scope attributes, tabular-nums)
 * that is the readable source of truth for every figure.
 *
 * Motion: SplitReveal headline, ScrollReveal child-stagger for the chart trio
 * and the table rows, AnimatedCounter for each headline column figure. All
 * primitives are reduced-motion safe and never strand content hidden; bar
 * heights are inline so the final value is always rendered. Every number is
 * mapped from RETENTION_SERIES in '@/data/experience' — none is hardcoded.
 */
export function RetentionDetail() {
  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + definition ────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Retention
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Retention rate, year by year.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              Retention is the percentage of enrolled students retained across the reporting year.
              The ACBSP-accredited MBA and DBA rates are shown alongside the institution-wide rate
              for 2021–2025.
            </p>
          </ScrollReveal>
        </header>

        {/* ── Column charts — one per series ──────────────────────────── */}
        <ScrollReveal
          stagger={0.1}
          className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
        >
          {RETENTION_CHARTS.map((chart) => (
            <div
              key={chart.key}
              className="rounded-2xl border border-border bg-background p-6 shadow-sm"
            >
              <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                {chart.title}
              </p>
              <div className="mt-5 flex h-40 items-end justify-between gap-2">
                {RETENTION_SERIES.map((row) => {
                  const value = row[chart.key];
                  return (
                    <div
                      key={row.year}
                      className="flex h-full flex-1 flex-col items-center justify-end gap-2"
                    >
                      <span className="font-display text-xs font-semibold text-foreground tabular-nums">
                        {value.toFixed(1)}
                      </span>
                      <div
                        className={`w-full rounded-t-md ${chart.bar}`}
                        style={{ height: `${value}%` }}
                        role="img"
                        aria-label={`${chart.title} retention, ${row.year}: ${value.toFixed(1)} percent`}
                      />
                      <span className="text-xs text-muted-foreground tabular-nums">{row.year}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </ScrollReveal>

        {/* ── Full grid — semantic source-of-truth table ──────────────── */}
        <div className="mt-14 overflow-x-auto rounded-2xl border border-border shadow-sm">
          <table className="w-full border-collapse text-left text-sm">
            <caption className="sr-only">
              Retention rate (%) by programme and year, 2021–2025.
            </caption>
            <thead className="bg-brand-navy text-white">
              <tr>
                <th
                  scope="col"
                  className="px-5 py-4 font-label text-xs tracking-[0.16em] text-white/70 uppercase"
                >
                  Year
                </th>
                <th
                  scope="col"
                  className="px-5 py-4 text-right font-label text-xs tracking-[0.16em] text-white/70 uppercase"
                >
                  MBA (ACBSP)
                </th>
                <th
                  scope="col"
                  className="px-5 py-4 text-right font-label text-xs tracking-[0.16em] text-white/70 uppercase"
                >
                  DBA (ACBSP)
                </th>
                <th
                  scope="col"
                  className="px-5 py-4 text-right font-label text-xs tracking-[0.16em] text-white/70 uppercase"
                >
                  Institution-wide
                </th>
              </tr>
            </thead>
            <ScrollReveal as="tbody" stagger={0.06} className="divide-y divide-border bg-background">
              {RETENTION_SERIES.map((row) => (
                <tr key={row.year} className="transition-colors duration-300 hover:bg-muted/30">
                  <th
                    scope="row"
                    className="px-5 py-4 font-display font-semibold text-foreground tabular-nums"
                  >
                    {row.year}
                  </th>
                  <td className="px-5 py-4 text-right text-foreground tabular-nums">
                    {row.mba.toFixed(1)}%
                  </td>
                  <td className="px-5 py-4 text-right text-foreground tabular-nums">
                    {row.dba.toFixed(1)}%
                  </td>
                  <td className="px-5 py-4 text-right text-foreground tabular-nums">
                    {row.institution.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </ScrollReveal>
          </table>
        </div>

        {/* ── Latest reporting-year headline ──────────────────────────── */}
        <ScrollReveal
          stagger={0.08}
          className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-3"
        >
          {RETENTION_CHARTS.map((chart) => {
            const latest = RETENTION_SERIES[RETENTION_SERIES.length - 1];
            return (
              <div
                key={chart.key}
                className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                  {chart.title}
                </p>
                <AnimatedCounter
                  value={latest[chart.key]}
                  decimals={1}
                  suffix="%"
                  className="mt-4 block font-display text-4xl leading-none font-semibold text-brand-navy md:text-5xl"
                />
                <p className="mt-3 text-xs leading-snug text-muted-foreground">
                  Retention · {latest.year}
                </p>
              </div>
            );
          })}
        </ScrollReveal>

        {/* ── Sourcing caption ────────────────────────────────────────── */}
        <p className="mt-10 border-t border-border pt-8 text-xs text-muted-foreground">
          Figures are institution-reported for 2021–2025.
        </p>
      </div>
    </section>
  );
}
